/**
 * Tenant-facing Storyboard generation errors.
 * Keep machine codes / raw schema paths for debug; never show bare JSON Schema
 * paths like `storyboards/0: 'style' is a required property` as primary UX.
 */

import type { TFunction } from 'i18next'

const SCHEMA_VALIDATION_CODES = new Set([
  'SCHEMA_VALIDATION_FAILED',
  'INVALID_STORYBOARD_STYLE',
  'INVALID_STORYBOARD_ARTIFACT',
  'OUTPUT_MISSING',
  'EMPTY_MODEL_OUTPUT',
])

export function looksLikeStoryboardJsonSchemaError(message: string | null | undefined): boolean {
  const m = String(message || '').toLowerCase()
  if (!m) return false
  return (
    m.includes('is a required property') ||
    m.includes('is not of type') ||
    m.includes('is not valid under any of the given schemas') ||
    m.includes("'style' is a required property") ||
    m.includes('additional properties are not allowed') ||
    m.includes('intake failed') ||
    /storyboards\/\d+/.test(m)
  )
}

export function looksLikeStoryboardIntakeSchemaError(message: string | null | undefined): boolean {
  const m = String(message || '').toLowerCase()
  return m.includes('intake failed') && m.includes('schema validation')
}

export function localizeStoryboardGenerationError(
  args: { code?: string | null; message?: string | null },
  t: TFunction<'canvas'>,
): string {
  const code = String(args.code || '').toUpperCase()
  const message = String(args.message || '').trim()

  if (
    looksLikeStoryboardIntakeSchemaError(message) ||
    (code === 'VALIDATION_ERROR' && message.toLowerCase().includes('intake failed'))
  ) {
    return t('storyboardErrors.intakeSchemaIncompatible')
  }
  if (code === 'SCHEMA_VALIDATION_FAILED' || looksLikeStoryboardJsonSchemaError(message)) {
    return t('storyboardErrors.schemaValidationFailed')
  }
  if (code === 'INVALID_STORYBOARD_STYLE') {
    return t('storyboardErrors.invalidStyle')
  }
  if (code === 'INVALID_STORYBOARD_ARTIFACT' || code === 'OUTPUT_MISSING' || code === 'EMPTY_MODEL_OUTPUT') {
    return t('storyboardErrors.invalidArtifact')
  }
  if (code === 'MODEL_REFUSAL') {
    return t('storyboardErrors.modelRefusal')
  }
  if (code === 'MISSING_PRIMARY_CONTEXT') {
    return t('storyboardErrors.missingPrimaryContext')
  }
  if (SCHEMA_VALIDATION_CODES.has(code)) {
    return t('storyboardErrors.schemaValidationFailed')
  }
  if (message) return message
  return t('storyboardErrors.generationFailed')
}

/** Dev-only: preserve technical schema detail without replacing tenant copy. */
export function logStoryboardGenerationErrorDev(
  code: string | null | undefined,
  technicalDetail: string | null | undefined,
): void {
  if (typeof console === 'undefined' || !console.warn) return
  if (!code && !technicalDetail) return
  console.warn('[storyboard-generation]', { error_code: code, technical_detail: technicalDetail })
}
